import { ReactNode } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "@localization/useTranslation";
import RiskOverAppetiteCard from "./riskOverAppetiteCard/RiskOverAppetiteCard";
import AssessedCoverageCard from "./assessedCoverageCard/AssessedCoverageCard";
import RiskExposureCard from "./riskExposureCard/RiskExposureCard";
import { RiskExposureCardTone } from "./riskExposureCard/RiskExposureCard.styles";
import { reportPanelBackground } from "./RiskReportChartPanel";
import { RiskReportCoverage, RiskReportExposureCards } from "./RiskManagementReport.types";
import CurrentExposureIcon from "./riskExposureCard/currentExposure.svg?react";
import AfterMitigationIcon from "./riskExposureCard/afterMitigation.svg?react";
import MitigationCostsIcon from "./riskExposureCard/mitigationCosts.svg?react";

interface RiskReportKpiCardsProps {
    stacked?: boolean;
    isLoading?: boolean;
    riskAppetiteLimit: number;
    risksAboveAppetite: number;
    isExceeded: boolean;
    coverage: RiskReportCoverage;
    // Null when exposure is not configured (None mode) — the exposure panel is hidden entirely.
    exposureCards: RiskReportExposureCards | null;
}

interface ExposureCard {
    key: string;
    header: string;
    icon: ReactNode;
    tone: RiskExposureCardTone;
    value: string;
    pillText?: string | null;
    pillArrow?: "up" | "down";
}

function RiskReportKpiCards({
    stacked = false,
    isLoading = false,
    riskAppetiteLimit,
    risksAboveAppetite,
    isExceeded,
    coverage,
    exposureCards,
}: RiskReportKpiCardsProps) {
    const { translateString } = useTranslation();
    const cardSize = stacked ? 12 : { xs: 12, lg: 6 };
    const exposureCardSize = stacked ? 12 : { xs: 12, lg: 4 };

    // Built unconditionally so the panel can render skeleton cards while the report loads — at that point the
    // exposure mode is unknown, so values fall back to empty and are ignored by the card's loading state. Once
    // loaded, the panel is hidden entirely when there is no exposure data (see showExposurePanel below).
    const isIncrease = exposureCards?.afterMitigationChange?.direction === "increase";
    const cards: Array<ExposureCard> = [
        {
            key: "currentExposure",
            header: exposureCards?.currentExposureHeader ?? translateString("riskManagementReportCurrentExposure"),
            icon: <CurrentExposureIcon />,
            tone: "danger",
            value: exposureCards?.currentExposure ?? "",
        },
        {
            key: "afterMitigation",
            header: translateString("afterMitigation"),
            icon: <AfterMitigationIcon />,
            tone: isIncrease ? "danger" : "success",
            value: exposureCards?.afterMitigation ?? "",
            pillText: exposureCards?.afterMitigationChange?.text,
            pillArrow: isIncrease ? "up" : "down",
        },
        {
            key: "mitigationCosts",
            header: translateString("riskManagementReportMitigationCosts"),
            icon: <MitigationCostsIcon />,
            tone: "info",
            value: exposureCards?.mitigationCosts ?? "",
            pillText: exposureCards?.mitigationCostsPlans,
        },
    ];

    const showExposurePanel = isLoading || exposureCards !== null;

    // In the PDF each panel is wrapped so the print engine keeps it whole (avoid-page-break) and top padding
    // survives at a page break, giving whichever panel lands first on a page a consistent margin above it.
    const renderPanel = (content: ReactNode) => {
        const panel = (
            <Box sx={{ padding: 2, backgroundColor: reportPanelBackground, borderRadius: "20px", mb: stacked ? 0 : 2 }}>
                <Grid container spacing={2}>
                    {content}
                </Grid>
            </Box>
        );

        if (stacked) {
            return (
                <Box className="avoid-page-break" sx={{ pt: 2 }}>
                    {panel}
                </Box>
            );
        }

        return panel;
    };

    return (
        <>
            {renderPanel(
                <>
                    <Grid size={cardSize}>
                        <RiskOverAppetiteCard
                            isLoading={isLoading}
                            riskAppetiteLimit={riskAppetiteLimit}
                            risksAboveAppetite={risksAboveAppetite}
                            isExceeded={isExceeded}
                        />
                    </Grid>
                    <Grid size={cardSize}>
                        <AssessedCoverageCard isLoading={isLoading} coverage={coverage} />
                    </Grid>
                </>
            )}

            {showExposurePanel &&
                renderPanel(
                    cards.map((card) => (
                        <Grid key={card.key} size={exposureCardSize}>
                            <RiskExposureCard
                                isLoading={isLoading}
                                header={card.header}
                                value={card.value}
                                pillText={card.pillText}
                                pillArrow={card.pillArrow}
                                tone={card.tone}
                                icon={card.icon}
                            />
                        </Grid>
                    ))
                )}
        </>
    );
}

export default RiskReportKpiCards;
